// SCR-QUESTS (E09-T03; ux/09 §1): «Посылка от дяди Вити», вкладки «День», «Неделя» (с 7-го уровня), «Достижения»,
// бонус «все 5», «Забрать» на месте и «Забрать всё (N)». Таймер дня — до 04:00 МСК по времени сервера.
// Прогресс считает сервер; WS `quest.progress` в договоре нет (Р-98) — список перечитывается при входе и возврате в игру.
// «Заменить» задание ждёт эндпоинта (Р-98) и не показывается.
import { useEffect, useState } from 'preact/hooks';
import type { Api, ResponseOf } from '@garages/contract';
import { useApp } from '../app/app.js';
import { I, type IconName, ICONS } from '../assets/icons.js';
import { amount, duration, num } from '../i18n/format.js';
import { hasKey, t as tr } from '../i18n/t.js';
import { ApiCallError } from '../net/api-client.js';
import { online, player, serverNow, setWallet, tutorialStep } from '../state/game.js';
import { nextDayStart, questsClaimable } from '../state/quests.js';
import type { ScreenProps } from '../ui/App.js';
import { ErrorBanner, ScreenTitle, Skeleton, useActionBar, useNow } from '../ui/kit.js';
import { showToast } from '../ui/toast.js';
import { flyGrant } from '../ui/fx.js';
import { FIRST_BOX, TEXT as TUT, tutT10Open } from '../tutorial/flow.js';
import { useLoad } from '../ui/use-load.js';

type QuestsResp = ResponseOf<Api['getQuests']>;
type Quest = QuestsResp['items'][number];
type Ach = ResponseOf<Api['getAchievements']>['items'][number];
type Grant = Quest['rewards'];
type Tab = 'day' | 'week' | 'ach';

export const WEEKLY_MIN_LEVEL = 7;

/** Порядок карточек (ux/09 §1.2): «Забрать» → в процессе → забранные. */
export function sortQuests(list: readonly Quest[]): Quest[] {
  const rank = (q: Quest) => (q.status === 'completed' ? 0 : q.status === 'active' ? 1 : 2);
  return [...list].sort((a, b) => rank(a) - rank(b) || (a.slot ?? 0) - (b.slot ?? 0));
}

/** «Идти» — туда, где делается шаг задания (ux/09 §1.2; ГД 09 §1). */
export function questTarget(questId: string): 'map' | 'SCR-INVENTORY' | 'SCR-CAR' | 'coop' | 'home' {
  const n = Number(questId.slice(4));
  if (questId.startsWith('Q-W-')) return [1, 3, 11, 13].includes(n) ? 'map' : n === 5 ? 'SCR-CAR' : n === 7 || n === 9 ? 'SCR-INVENTORY' : n === 6 ? 'home' : 'coop';
  if (n <= 10) return 'map';
  if (n === 11 || n === 13 || n === 14 || n === 18) return 'SCR-INVENTORY';
  if (n === 12) return 'home';
  if (n >= 15 && n <= 17) return 'SCR-CAR';
  return 'coop';
}

export function GrantIcons(p: { g: Grant | null | undefined; size?: number }) {
  if (!p.g) return null;
  return (
    <span class="grant-icons">
      {Object.entries(p.g.currencies ?? {}).map(([k, v]) => <span key={k} class="grant">{k in ICONS ? <I n={k as IconName} size={p.size ?? 16} /> : null}{num(v ?? 0)}</span>)}
      {(p.g.items ?? []).map((i) => <span key={i.defId} class="grant">{tr(hasKey(`CL-ITEM-${i.defId}`) ? `CL-ITEM-${i.defId}` : 'CL-QUEST-ITEM')}{i.qty > 1 ? ` ×${i.qty}` : ''}</span>)}
    </span>
  );
}

const grantText = (g: Grant) => Object.entries(g.currencies ?? {}).map(([k, v]) => amount(k, v ?? 0)).join(', ');

export function QuestsScreen(_p: ScreenProps) {
  const app = useApp();
  const now = useNow(1000);
  const lvl = player.value?.level ?? 1;
  const [tab, setTab] = useState<Tab>('day');
  const [busy, setBusy] = useState<string | null>(null);
  const [dayAt, setDayAt] = useState(() => nextDayStart(serverNow()));
  const load = useLoad(() => app.call('getQuests'), { onData: (d) => { questsClaimable.value = countClaimable(d); } });
  const ach = useLoad(() => app.call('getAchievements'));
  const d = load.data;
  const daily = d ? sortQuests(d.items.filter((q) => q.periodKey.startsWith('D:'))) : [];
  const weekly = d ? sortQuests(d.items.filter((q) => q.periodKey.startsWith('W:'))) : [];
  const list = tab === 'day' ? daily : weekly;
  const ready = list.filter((q) => q.status === 'completed');
  const newDay = now >= dayAt;
  // Возврат в игру — свежий прогресс (WS quest.progress нет, Р-98).
  useEffect(() => {
    const f = () => { if (!document.hidden) load.reload(); };
    document.addEventListener('visibilitychange', f);
    return () => document.removeEventListener('visibilitychange', f);
  }, []);

  const claim = async (q: Quest, silent = false) => {
    setBusy(q.questId);
    try {
      const r = await app.call('claimQuest', { params: { questId: q.questId as 'Q-D-01' }, body: { periodKey: q.periodKey } });
      flyGrant(document.querySelector(`[data-testid="quest-${q.questId}"] .btn`) ?? document.querySelector(`[data-testid="quest-${q.questId}"]`), r.rewards?.currencies);
      setWallet(r.wallet);
      app.haptic('success');
      if (!silent) showToast(tr('CL-QUEST-GOT', { what: grantText(r.rewards) || tr('CL-QUEST-ITEM') }), 'success');
    } catch (e) {
      const code = e instanceof ApiCallError ? e.code : null;
      if (code === 'NOT_COMPLETED') showToast(tr('CL-QUEST-NOT-DONE', { progress: num(q.progress), target: num(q.target) }));
      else if (code !== 'ALREADY_CLAIMED') app.handleError(e);
    } finally { setBusy(null); load.reload(); }
  };
  const claimAll = async () => { for (const q of ready) await claim(q, true); showToast(tr('CL-QUEST-GOT-ALL', { n: ready.length }), 'success'); };
  const claimAll5 = async () => {
    if (!d) return;
    setBusy('ALL5');
    try {
      const r = await app.call('claimQuest', { params: { questId: 'ALL5' }, body: { periodKey: daily[0]?.periodKey ?? '' } });
      setWallet(r.wallet);
      app.haptic('heavy');
      app.nav.openSheet('MOD-REWARD', { grant: r.rewards });
    } catch (e) {
      if (!(e instanceof ApiCallError && e.code === 'ALREADY_CLAIMED')) app.handleError(e);
    } finally { setBusy(null); load.reload(); }
  };
  const openBox = async () => {
    setBusy('BOX');
    try {
      const r = await app.call('claimDailyBox', { body: {} });
      setWallet(r.wallet);
      app.haptic('success');
      const currencies: Record<string, number> = {};
      const items: { defId: string; qty: number }[] = [];
      for (const x of r.drops) {
        if (x.kind === 'currency') currencies[x.currency] = (currencies[x.currency] ?? 0) + x.amount;
        else if (x.kind === 'stack') items.push({ defId: x.defId, qty: x.qty });
        else items.push({ defId: x.item.defId, qty: 1 });
      }
      app.nav.openSheet('MOD-REWARD', { grant: { currencies, items } });
    } catch (e) {
      if (!(e instanceof ApiCallError && e.code === 'ALREADY_CLAIMED')) app.handleError(e);
    } finally { setBusy(null); load.reload(); }
  };
  const go = (q: Quest) => {
    const t = questTarget(q.questId);
    if (t === 'map' || t === 'coop' || t === 'home') app.nav.selectTab(t); else app.nav.push(t);
  };
  useActionBar(tab !== 'ach' && ready.length >= 2 ? { main: { text: tr('CL-QUEST-CLAIM-ALL', { n: ready.length }), testId: 'quests-claim-all', disabled: !online.value || !!busy, onClick: claimAll } } : {});

  const box = d?.dailyBox;
  const all5 = d?.allFive;
  return (
    <>
      <ScreenTitle right={<span class="small" data-testid="quests-timer">{tr('CL-QUEST-NEW-DAY', { time: duration(Math.max(0, dayAt - now)) })}</span>}>{tr('UI-NAV-QUESTS')}</ScreenTitle>
      <div class="content screen-bg" data-testid="SCR-QUESTS">
        <div class="page col">
          {newDay ? <div class="banner" data-testid="quests-new-day"><span style={{ flex: 1 }}>{tr('CL-QUEST-NEW-DAY-BANNER')}</span><button class="btn small outline" style={{ width: 'auto' }} onClick={() => { setDayAt(nextDayStart(serverNow())); load.reload(); }}>{tr('CL-QUEST-REFRESH')}</button></div> : null}
          {tutorialStep.value === 'T10' ? <FirstBox /> : null}
          {box ? (
            <div class="card row quest-box" data-testid="daily-box">
              <span class="box-ill" aria-hidden><I n="gift" size={40} /></span>
              <span class="col" style={{ flex: 1, gap: 2 }}><b>{tr('CL-QUEST-BOX')}</b><span class="small muted">{box.available ? tr('CL-QUEST-BOX-HINT') : tr('CL-QUEST-BOX-NEXT', { time: duration(Math.max(0, Date.parse(box.nextAt) - now)) })}</span></span>
              <button class="btn small accent" style={{ width: 'auto' }} data-testid="daily-box-open" disabled={!box.available || !online.value || !!busy} onClick={openBox}>{box.available ? tr('CL-QUEST-OPEN') : tr('CL-QUEST-OPENED')}</button>
            </div>
          ) : null}
          <div class="chips" role="tablist">
            <button class={`chip ${tab === 'day' ? 'on' : ''}`} role="tab" aria-selected={tab === 'day'} data-testid="qtab-day" onClick={() => setTab('day')}>{tr('CL-QUEST-TAB-DAY')}</button>
            <button class={`chip ${tab === 'week' ? 'on' : ''}`} role="tab" aria-selected={tab === 'week'} data-testid="qtab-week" disabled={lvl < WEEKLY_MIN_LEVEL} onClick={() => setTab('week')}>{lvl < WEEKLY_MIN_LEVEL ? tr('CL-QUEST-TAB-WEEK-LOCKED', { n: WEEKLY_MIN_LEVEL }) : tr('CL-QUEST-TAB-WEEK')}</button>
            <button class={`chip ${tab === 'ach' ? 'on' : ''}`} role="tab" aria-selected={tab === 'ach'} data-testid="qtab-ach" onClick={() => setTab('ach')}>{tr('CL-QUEST-TAB-ACH')}</button>
          </div>
          {tab === 'ach' ? <Achievements load={ach} /> : (
            <>
              {load.error && !d ? <ErrorBanner what={tr('CL-QUEST-WHAT')} onRetry={load.reload} /> : null}
              {!d && load.loading ? <Skeleton h={72} n={5} /> : null}
              {tab === 'day' && all5 ? (
                <div class="card col" data-testid="all5">
                  <div class="row"><b style={{ flex: 1 }}>{tr('CL-QUEST-ALL', { n: 5 })}</b><GrantIcons g={all5.rewards} /></div>
                  <div class="progress"><i style={{ width: `${Math.min(100, (all5.done / 5) * 100)}%` }} /></div>
                  <div class="row"><span class="small muted" style={{ flex: 1 }}>{tr('CL-QUEST-OF', { k: Math.min(5, all5.done), n: 5 })}</span>
                    {all5.claimed ? <span class="small">✓</span> : all5.done >= 5 ? <button class="btn small accent" style={{ width: 'auto' }} data-testid="all5-claim" disabled={!online.value || !!busy} onClick={claimAll5}>{tr('CL-QUEST-CLAIM')}</button> : null}
                  </div>
                </div>
              ) : null}
              {d && !list.length ? <div class="empty">{tr('CL-QUEST-EMPTY')}</div> : null}
              {d && list.length && list.every((q) => q.status === 'claimed') ? <div class="banner">{tr('CL-QUEST-DONE-TODAY')}</div> : null}
              {list.filter((q) => q.status !== 'claimed').map((q) => <QuestCard key={q.questId} q={q} now={now} busy={busy === q.questId} onClaim={() => claim(q)} onGo={() => go(q)} />)}
              {list.some((q) => q.status === 'claimed') ? (
                <details class="done-list"><summary class="small muted">{tr('CL-QUEST-DONE-N', { n: list.filter((q) => q.status === 'claimed').length })}</summary>
                  {list.filter((q) => q.status === 'claimed').map((q) => <QuestCard key={q.questId} q={q} now={now} busy={false} onClaim={() => {}} onGo={() => {}} />)}
                </details>
              ) : null}
            </>
          )}
        </div>
      </div>
    </>
  );
}

/** T10 (ux/11 §1.3): «Первая посылка» — фиксированный состав; вместо «Шансы» — «Что внутри» (economy/04 §7.2). */
function FirstBox() {
  const app = useApp();
  const [inside, setInside] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <div class="card col quest-box first-box" data-tut="tut-first-box" data-testid="first-box">
      <div class="row" style={{ gap: 10 }}>
        <span class="box-ill" aria-hidden><I n="gift" size={40} /></span>
        <b style={{ flex: 1 }}>{tr('CL-QUEST-FIRST-BOX')}</b>
        <button class="btn small accent" style={{ width: 'auto' }} data-testid="first-box-open" disabled={busy || !online.value}
          onClick={async () => { setBusy(true); try { await tutT10Open(app); } finally { setBusy(false); } }}>{TUT.T10open}</button>
      </div>
      <button class="link-btn small" data-testid="first-box-inside" onClick={() => setInside(!inside)}>{tr('CL-QUEST-INSIDE')}</button>
      {inside ? <GrantIcons g={FIRST_BOX} size={18} /> : null}
    </div>
  );
}

export function countClaimable(d: QuestsResp): number {
  return d.items.filter((q) => q.status === 'completed').length + (d.allFive && !d.allFive.claimed && d.allFive.done >= 5 ? 1 : 0) + (d.dailyBox.available ? 1 : 0);
}

function QuestCard(p: { q: Quest; now: number; busy: boolean; onClaim: () => void; onGo: () => void }) {
  const { q } = p;
  const pct = Math.min(100, (q.progress / q.target) * 100);
  const soon = q.status === 'completed' && Date.parse(q.resetsAt) - p.now < 3_600_000;
  return (
    <div class={`card quest ${q.status}`} data-testid={`quest-${q.questId}`}>
      <span class="col" style={{ flex: 1, gap: 4, minWidth: 0 }}>
        <span>{tr(hasKey(`CL-QUEST-${q.questId}`) ? `CL-QUEST-${q.questId}` : 'CL-QUEST-UNKNOWN', { n: q.target, nf: num(q.target) })}</span>
        <div class="progress"><i style={{ width: `${pct}%` }} /></div>
        <span class="row small muted" style={{ gap: 8 }}><span style={{ flex: 1 }}>{num(Math.min(q.progress, q.target))} / {num(q.target)}</span><GrantIcons g={q.rewards} size={14} /></span>
        {soon ? <span class="small warn-text">{tr('CL-QUEST-TO-MAIL')}</span> : null}
      </span>
      {q.status === 'claimed' ? <I n="check" size={22} />
        : q.status === 'completed' ? <button class="btn small accent" style={{ width: 'auto' }} data-testid={`quest-claim-${q.questId}`} disabled={!online.value || p.busy} onClick={p.onClaim}>{tr('CL-QUEST-CLAIM')}</button>
          : <button class="btn small outline" style={{ width: 'auto' }} data-testid={`quest-go-${q.questId}`} onClick={p.onGo}>{tr('CL-QUEST-GO')}</button>}
    </div>
  );
}

function Achievements(p: { load: { data: { items: Ach[] } | null; loading: boolean; error: string | null; reload: () => void } }) {
  const app = useApp();
  const [busy, setBusy] = useState<string | null>(null);
  const items = p.load.data?.items ?? [];
  const sorted = [...items].sort((a, b) => Number(b.claimable) - Number(a.claimable) || Number(a.tier === null) - Number(b.tier === null));
  const claim = async (a: Ach) => {
    if (!a.tier) return;
    setBusy(a.achievementId);
    try {
      const r = await app.call('claimAchievement', { params: { achievementId: a.achievementId as 'A-01' }, body: { tier: a.tier } });
      setWallet(r.wallet);
      app.haptic('success');
      app.nav.openSheet('MOD-REWARD', { grant: r.rewards });
    } catch (e) {
      const code = e instanceof ApiCallError ? e.code : null;
      if (code === 'NOT_COMPLETED') showToast(tr('CL-QUEST-NOT-DONE', { progress: num(a.progress), target: num(a.target ?? 0) }));
      else if (code !== 'ALREADY_CLAIMED') app.handleError(e);
    } finally { setBusy(null); p.load.reload(); }
  };
  if (p.load.error && !p.load.data) return <ErrorBanner what={tr('CL-QUEST-TAB-ACH')} onRetry={p.load.reload} />;
  if (!p.load.data) return <Skeleton h={64} n={4} />;
  return (
    <div class="col" data-testid="achievements">
      {sorted.map((a) => (
        <div key={a.achievementId} class="card quest" data-testid={`ach-${a.achievementId}`}>
          <span class="col" style={{ flex: 1, gap: 4, minWidth: 0 }}>
            <b>{tr(hasKey(`CL-ACH-${a.achievementId}`) ? `CL-ACH-${a.achievementId}` : 'CL-QUEST-UNKNOWN')}</b>
            <span class="small muted">{tr('CL-ACH-TIER', { k: a.claimedTiers, n: a.tiers })}{a.target ? ` · ${num(Math.min(a.progress, a.target))} / ${num(a.target)}` : ''}</span>
            {a.target ? <div class="progress"><i style={{ width: `${Math.min(100, (a.progress / a.target) * 100)}%` }} /></div> : null}
          </span>
          {a.tier === null ? <I n="check" size={22} />
            : a.claimable ? <button class="btn small accent" style={{ width: 'auto' }} data-testid={`ach-claim-${a.achievementId}`} disabled={!online.value || busy === a.achievementId} onClick={() => claim(a)}>{tr('CL-QUEST-CLAIM')}</button>
              : <GrantIcons g={a.rewards} size={14} />}
        </div>
      ))}
    </div>
  );
}
